import React, {useEffect, useState} from 'react';
import TextInput from 'components/inputs/TextInput';
import DateInput from 'components/inputs/DateInput';
import BlueButton from 'components/BlueButton';
import {PayerService} from 'services/payerService';
import {Payer} from '../../types/payer';
import CustomSelect from 'components/inputs/CustomSelect';
import PlusIcon from 'assets/images/icons/PlusIcon';
import WhiteButton from 'components/WhiteButton';
import {ClientInsurance} from '../../types/clientInsurance';
import FormSkeleton from 'components/FormSkeleton';

interface ClientInsuranceFormProps {
  closeModal: () => void;
  clientData?: any;
  setClientData?: (data: any) => void;
  onBack: () => void;
  onSave: () => void;
  isSaving: boolean;
  isInsuranceValid: boolean;
}

const ClientInsuranceForm: React.FC<ClientInsuranceFormProps> = ({
  closeModal,
  clientData,
  setClientData,
  onBack,
  onSave,
  isInsuranceValid,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [payersOptions, setPayersOptions] = useState<
    {label: string; value: string}[]
  >([]);

  const [showPrimaryInputs, setShowPrimaryInputs] = useState(false);
  const [showSecondaryInputs, setShowSecondaryInputs] = useState(false);

  const [primaryCoverage, setPrimaryCoverage] = useState<any>(
    clientData.clientInsurances?.filter(
      (ic: ClientInsurance) => ic.insuranceType === 'primary',
    )[0] || {},
  );
  const [secondaryCoverage, setSecondaryCoverage] = useState<any>(
    clientData.clientInsurances?.filter(
      (ic: ClientInsurance) => ic.insuranceType === 'secondary',
    )[0] || {},
  );

  useEffect(() => {
    setShowPrimaryInputs(Object.keys(primaryCoverage).length > 0);
    setShowSecondaryInputs(Object.keys(secondaryCoverage).length > 0);
  }, [primaryCoverage, secondaryCoverage]);

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const payersResponse = await PayerService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const payerOpts =
          payersResponse.data?.map((payer: Payer) => ({
            label: payer.name,
            value: payer.id,
          })) || [];
        setPayersOptions(payerOpts);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (setClientData) {
      setClientData({
        ...clientData,
        clientInsurances: [primaryCoverage, secondaryCoverage].filter(
          cov => Object.keys(cov).length > 0,
        ),
      });
    }
  }, [primaryCoverage, secondaryCoverage]);

  const handlePrimaryChange = (field: string, value: any) => {
    setPrimaryCoverage({...primaryCoverage, [field]: value});
  };

  const handleSecondaryChange = (field: string, value: any) => {
    setSecondaryCoverage({...secondaryCoverage, [field]: value});
  };

  const handleShowPrimaryInputs = () => {
    setShowPrimaryInputs(true);
    if (
      primaryCoverage === undefined ||
      Object.keys(primaryCoverage).length === 0
    ) {
      setPrimaryCoverage({insuranceType: 'primary'});
    }
  };

  const handleShowSecondaryInputs = () => {
    setShowSecondaryInputs(true);
    if (
      secondaryCoverage === undefined ||
      Object.keys(secondaryCoverage).length === 0
    ) {
      setSecondaryCoverage({insuranceType: 'secondary'});
    }
  };

  return (
    <div className="px-4 w-full">
      {isLoading ? (
        <FormSkeleton rows={2} />
      ) : (
        <form>
          <div className="border-b border-slate-200 mt-2 flex items-center justify-between mb-4 ">
            <h2 className="text-m font-semibold pt-6 pb-2 text-slate-400  ">
              Primary Coverage
            </h2>
            <BlueButton icon={<PlusIcon />} onClick={handleShowPrimaryInputs} />
          </div>
          {showPrimaryInputs && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-4">
                <DateInput
                  id={`asOfDate-${0}`}
                  type="date"
                  label="As of *"
                  required={true}
                  value={primaryCoverage.asOfDate || ''}
                  allowPastDates={true}
                  placeholder="MM/DD/YYYY"
                  onChange={e => handlePrimaryChange('asOfDate', e.target.value)}
                />
                <DateInput
                  id={`untilDate-${0}`}
                  type="date"
                  label="Until *"
                  required={true}
                  value={primaryCoverage.untilDate || ''}
                  allowPastDates={true}
                  placeholder="MM/DD/YYYY"
                  onChange={e => handlePrimaryChange('untilDate', e.target.value)}
                />
                <div className="flex flex-col gap-[0.625rem] w-full">
                  <label
                    htmlFor="enableHeader"
                    className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                    Payers
                    <span className="text-slate-800">*</span>
                  </label>
                  <CustomSelect
                    options={payersOptions}
                    isMulti={false}
                    value={primaryCoverage.payerId || ''}
                    onChange={selectedOption =>
                      handlePrimaryChange(
                        'payerId',
                        selectedOption ? selectedOption.value : '',
                      )
                    }
                  />
                </div>
                <TextInput
                  id={`policyHolder-${0}`}
                  type="text"
                  label="Policy Holder"
                  value={primaryCoverage.policyHolder}
                  onChange={e =>
                    handlePrimaryChange('policyHolder', e.target.value)
                  }
                />
                <TextInput
                  id={`policyNumber-${0}`}
                  type="text"
                  label="Policy Number"
                  value={primaryCoverage.policyNumber}
                  onChange={e =>
                    handlePrimaryChange('policyNumber', e.target.value)
                  }
                />
                <TextInput
                  id={`groupNumber-0`}
                  type="text"
                  label="Group Number"
                  value={primaryCoverage.groupNumber}
                  onChange={e =>
                    handlePrimaryChange('groupNumber', e.target.value)
                  }
                />
                <TextInput
                  id={`planYear-${0}`}
                  type="text"
                  label="Plan Year"
                  value={primaryCoverage.planYear}
                  onChange={e => handlePrimaryChange('planYear', e.target.value)}
                />
              </div>
            </>
          )}
          <div className="border-b border-slate-200 mt-2 flex items-center justify-between mb-4 ">
            <h2 className="text-m font-semibold pt-6 pb-2 text-slate-400  ">
              Secondary Coverage
            </h2>
            <BlueButton icon={<PlusIcon />} onClick={handleShowSecondaryInputs} />
          </div>
          {showSecondaryInputs && (
            <>
              <div
                key={1}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <DateInput
                    id={`asOfSec-${1}`}
                    type="date"
                    label="As of *"
                    required={true}
                    value={secondaryCoverage.asOfDate}
                    allowPastDates={true}
                    placeholder="M/D/Y"
                    onChange={e =>
                      handleSecondaryChange('asOfDate', e.target.value)
                    }
                  />
                  <DateInput
                    id={`untilSec-${1}`}
                    type="date"
                    label="Until *"
                    required={true}
                    value={secondaryCoverage.untilDate}
                    allowPastDates={true}
                    placeholder="M/D/Y"
                    onChange={e =>
                      handleSecondaryChange('untilDate', e.target.value)
                    }
                  />
                </div>
                <div className="flex flex-col gap-[0.625rem] w-full">
                  <label
                    htmlFor="enableHeader"
                    className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                    Payers
                    <span className="text-slate-800">*</span>
                  </label>
                  <CustomSelect
                    options={payersOptions}
                    isMulti={false}
                    value={secondaryCoverage.payerId || ''}
                    onChange={selectedOption =>
                      handleSecondaryChange(
                        'payerId',
                        selectedOption ? selectedOption.value : '',
                      )
                    }
                  />
                </div>
                <TextInput
                  id={`policyHolderSec-${1}`}
                  type="text"
                  label="Policy Holder"
                  value={secondaryCoverage.policyHolder}
                  onChange={e =>
                    handleSecondaryChange('policyHolder', e.target.value)
                  }
                />
                <TextInput
                  id={`policyNumberSec-${1}`}
                  type="text"
                  label="Policy Number"
                  value={secondaryCoverage.policyNumber}
                  onChange={e =>
                    handleSecondaryChange('policyNumber', e.target.value)
                  }
                />
                <TextInput
                  id={`groupNumberSec-${1}`}
                  type="text"
                  label="Group Number"
                  value={secondaryCoverage.groupNumber}
                  onChange={e =>
                    handleSecondaryChange('groupNumber', e.target.value)
                  }
                />
                <TextInput
                  id={`planYearSec-${1}`}
                  type="text"
                  label="Plan Year"
                  value={secondaryCoverage.planYear}
                  onChange={e =>
                    handleSecondaryChange('planYear', e.target.value)
                  }
                />
              </div>
            </>
          )}
        </form>
      )}
      <footer className="flex-shrink-0 pb-4 pt-6 flex justify-end gap-2  w-full">
        <WhiteButton onClick={closeModal} label="Cancel" />
        <WhiteButton onClick={onBack} label="Back" />
        <BlueButton
          onClick={onSave}
          label={'Save'}
          inputId="modal-blue-button"
          disabled={!isInsuranceValid}
        />
      </footer>
    </div>
  );
};

export default ClientInsuranceForm;
